import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import QRGenerator from './QRGenerator';

interface RepDashboardProps {
  userId: string;
  profileId: string;
  nombreCompleto?: string;
}

interface RepData {
  id: string;
  rep_code: string;
  estado: string;
  comision_porcentaje: number;
  balance_pendiente: number;
  balance_pagado: number;
  total_ventas: number;
  total_comisiones: number;
  created_at: string;
}

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  size: number;
  rotation: number;
}

const BASE_URL = 'https://islavende.com';

const CONFETTI_COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#f97316', '#06b6d4'];

const RepDashboard = ({ userId, profileId, nombreCompleto }: RepDashboardProps) => {
  const [repData, setRepData] = useState<RepData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activando, setActivando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeQRTab, setActiveQRTab] = useState<'principal' | 'producto'>('principal');
  const [productoUrl, setProductoUrl] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPiece[]>([]);

  useEffect(() => {
    fetchRepData();
  }, [userId]);

  const fetchRepData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('representatives')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching rep data:', error);
    } else {
      setRepData(data);
    }
    setLoading(false);
  };

  const generarRepCode = useCallback(() => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    let code = '';
    for (let i = 0; i < 4; i++) code += letters[Math.floor(Math.random() * letters.length)];
    for (let i = 0; i < 4; i++) code += digits[Math.floor(Math.random() * digits.length)];
    return code;
  }, []);

  const launchConfetti = useCallback(() => {
    const pieces: ConfettiPiece[] = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 0.8,
      size: Math.random() * 8 + 6,
      rotation: Math.random() * 360,
    }));
    setConfettiPieces(pieces);
    setTimeout(() => setConfettiPieces([]), 3500);
  }, []);

  const handleActivar = async () => {
    setActivando(true);
    setError(null);
    try {
      const repCode = generarRepCode();
      const { data, error: insertError } = await supabase
        .from('representatives')
        .insert({
          user_id: userId,
          profile_id: profileId,
          rep_code: repCode,
          estado: 'activo',
          comision_porcentaje: 20,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      launchConfetti();
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setRepData(data);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Error al activar el programa');
    } finally {
      setActivando(false);
    }
  };

  const repLink = repData ? `${BASE_URL}/?rep=${repData.rep_code}` : '';

  const handleCopyLink = async () => {
    if (!repLink) return;
    await navigator.clipboard.writeText(repLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const productoLinkConRep = productoUrl && repData
    ? (() => {
        try {
          const url = new URL(productoUrl.startsWith('http') ? productoUrl : `${BASE_URL}${productoUrl}`);
          url.searchParams.set('rep', repData.rep_code);
          return url.toString();
        } catch {
          return `${BASE_URL}${productoUrl}?rep=${repData.rep_code}`;
        }
      })()
    : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-10 h-10 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Estado: No es representante aún
  if (!repData) {
    return (
      <div className="relative">
        {/* Confetti overlay */}
        {confettiPieces.length > 0 && (
          <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
            {confettiPieces.map((piece) => (
              <div
                key={piece.id}
                className="absolute animate-bounce"
                style={{
                  left: `${piece.x}%`,
                  top: '-20px',
                  width: `${piece.size}px`,
                  height: `${piece.size}px`,
                  backgroundColor: piece.color,
                  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  transform: `rotate(${piece.rotation}deg)`,
                  animation: `confettiFall ${1.5 + piece.delay}s ease-in forwards`,
                  animationDelay: `${piece.delay}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* Success overlay */}
        {showSuccess && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="bg-white rounded-2xl p-10 text-center max-w-sm mx-4 pointer-events-auto animate-pulse">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Bienvenido al equipo!</h3>
              <p className="text-gray-500 text-sm">Tu perfil de Representante está activo. ¡A generar comisiones!</p>
            </div>
          </div>
        )}

        <style>{`
          @keyframes confettiFall {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `}</style>

        <div className="text-center py-12 max-w-lg mx-auto">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <i className="ri-user-star-line text-4xl text-amber-600"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Programa de Representantes</h3>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            Únete al programa y gana <strong>20% de comisión</strong> por cada venta que generes compartiendo productos de IslaVende.
          </p>

          <div className="bg-gray-50 rounded-xl p-5 mb-6 text-left space-y-3">
            {[
              { icon: 'ri-qr-code-line', text: 'Recibe tu ID único y QR personal al instante' },
              { icon: 'ri-share-line', text: 'Comparte links y QRs de cualquier producto' },
              { icon: 'ri-money-dollar-circle-line', text: 'Gana 20% de comisión por cada venta generada' },
              { icon: 'ri-bar-chart-line', text: 'Monitorea tus ventas y comisiones en tiempo real' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                  <i className={`${item.icon} text-gray-600`}></i>
                </div>
                {item.text}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700">
              <i className="ri-error-warning-line mr-2"></i>{error}
            </div>
          )}

          <button
            onClick={handleActivar}
            disabled={activando || showSuccess}
            className="px-8 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 text-base"
          >
            {activando ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Activando...
              </span>
            ) : (
              <>
                <i className="ri-user-star-line mr-2"></i>
                ¡Activar mi Perfil de Representante!
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Estado: Ya es representante
  return (
    <div className="space-y-6">
      {/* Header con ID y estado */}
      <div className="bg-gray-900 rounded-xl p-5 text-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">Tu ID de Representante</p>
            <h2 className="text-3xl font-bold tracking-wider">{repData.rep_code}</h2>
            <p className="text-gray-400 text-xs mt-1">
              Activo desde {new Date(repData.created_at).toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
              repData.estado === 'activo' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              <i className={`${repData.estado === 'activo' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} mr-1`}></i>
              {repData.estado === 'activo' ? 'Activo' : 'Suspendido'}
            </span>
            <span className="px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-full text-xs font-bold">
              {repData.comision_porcentaje}% comisión
            </span>
          </div>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: 'ri-shopping-cart-line', label: 'Ventas Logradas', value: repData.total_ventas.toString(), color: 'bg-gray-100', iconColor: 'text-gray-700' },
          { icon: 'ri-money-dollar-circle-line', label: 'Comisiones Ganadas', value: `$${repData.total_comisiones.toFixed(2)}`, color: 'bg-green-100', iconColor: 'text-green-600' },
          { icon: 'ri-time-line', label: 'Pendiente de Pago', value: `$${repData.balance_pendiente.toFixed(2)}`, color: 'bg-amber-100', iconColor: 'text-amber-600' },
          { icon: 'ri-checkbox-circle-line', label: 'Comisiones Pagadas', value: `$${repData.balance_pagado.toFixed(2)}`, color: 'bg-emerald-100', iconColor: 'text-emerald-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
              <i className={`${stat.icon} text-xl ${stat.iconColor}`}></i>
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Link principal */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <i className="ri-links-line text-gray-600"></i>
          Tu Link Principal de IslaVende
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 font-mono truncate">
            {repLink}
          </div>
          <button
            onClick={handleCopyLink}
            className="px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Comparte este link y gana comisión por cualquier compra que se realice
        </p>
      </div>

      {/* QR Codes */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-qr-code-line text-gray-600"></i>
          Códigos QR
        </h3>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
          {[
            { id: 'principal', label: 'QR Principal' },
            { id: 'producto', label: 'QR por Artículo' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveQRTab(tab.id as 'principal' | 'producto')}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeQRTab === tab.id ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeQRTab === 'principal' && (
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <QRGenerator
              url={repLink}
              label={repLink}
              size={180}
              downloadName={`qr-rep-${repData.rep_code}`}
            />
            <div className="flex-1 space-y-3">
              <h4 className="font-semibold text-gray-900">QR Principal de IslaVende</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Este QR lleva a la página principal de IslaVende con tu código de representante. 
                Cualquier compra que se realice después de escanearlo te genera comisión.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700 flex items-start gap-2">
                  <i className="ri-lightbulb-line flex-shrink-0 mt-0.5"></i>
                  <span>Imprime este QR y colócalo en tu negocio, tarjeta personal o compártelo por WhatsApp para maximizar tus ventas.</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {activeQRTab === 'producto' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                URL del artículo en IslaVende
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={productoUrl}
                  onChange={(e) => setProductoUrl(e.target.value)}
                  placeholder="Ej: /producto/abc123 o https://islavende.com/producto/abc123"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Copia la URL del artículo desde la barra del navegador
              </p>
            </div>

            {productoLinkConRep ? (
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <QRGenerator
                  url={productoLinkConRep}
                  label={productoLinkConRep}
                  size={180}
                  downloadName={`qr-producto-${repData.rep_code}`}
                />
                <div className="flex-1 space-y-3">
                  <h4 className="font-semibold text-gray-900">Tu Link de Venta</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-700 break-all">
                    {productoLinkConRep}
                  </div>
                  <p className="text-sm text-gray-600">
                    Comparte este QR o link específico del artículo. Si alguien compra usando este link, recibes tu comisión automáticamente.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <i className="ri-qr-code-line text-4xl text-gray-300 mb-2 block"></i>
                <p className="text-sm text-gray-500">Ingresa la URL de un artículo para generar su QR personalizado</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instrucciones */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <i className="ri-information-line text-gray-500"></i>
          ¿Cómo funciona el rastreo?
        </h4>
        <div className="space-y-2 text-sm text-gray-600">
          <p className="flex items-start gap-2">
            <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
            Cuando alguien visita IslaVende usando tu link o QR, su sesión queda vinculada a tu código por 30 días.
          </p>
          <p className="flex items-start gap-2">
            <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
            Si esa persona realiza una compra, el sistema asigna automáticamente el {repData.comision_porcentaje}% de comisión a tu balance.
          </p>
          <p className="flex items-start gap-2">
            <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
            Las comisiones se pagan mensualmente por ATH Móvil o transferencia bancaria.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RepDashboard;
